import { InsurerReportEntry } from "types/insurerReports";
import { hasPaymentPlanEntry, isEntryMatched } from "../insurerReportUtils";

export interface EntryColumnConfig {
  columnId: string;
  width: number;
  reorderable: boolean;
  resizable: boolean;
  sortable: boolean;
  label: string;
  cellType: string;
  additionalInfo?: {
    rowIdField?: string;
    redColorFn?: (entry: any) => boolean;
    gosLink?: {
      idField: string;
      linkType: "policy" | "client" | "payer" | "insurer";
    };
  };
}

const isMatched = (entry: InsurerReportEntry) => isEntryMatched(entry);
const isPlanMatched = (entry: InsurerReportEntry) => hasPaymentPlanEntry(entry);

// An entry cleared through a payment is compared with that payment's premium,
// otherwise with the matched installment's (gross) premium.
const premiumAmountCounterpart = (entry: InsurerReportEntry) =>
  entry.paymentId !== null && entry.paymentId !== undefined
    ? entry.premiumAmountPayment
    : entry.premiumAmountPaymentPlanEntry;

export const insurerReportEntriesColumnConfig: EntryColumnConfig[] = [
  {
    columnId: "ordinalNumber",
    width: 40,
    reorderable: false,
    resizable: true,
    label: "",
    cellType: "ordinalnumber",
    sortable: false,
  },
  {
    columnId: "clientCode",
    width: 100,
    reorderable: true,
    resizable: true,
    sortable: true,
    label: "pct.clientCode.label",
    cellType: "customtext",
  },
  {
    columnId: "clientName",
    width: 200,
    reorderable: true,
    resizable: true,
    sortable: true,
    label: "pct.clientName.label",
    cellType: "customtext",
    additionalInfo: {
      // clientId is resolved from the matched payment / installment, so unmatched entries have no link
      gosLink: {
        idField: "clientId",
        linkType: "client",
      },
    },
  },
  {
    columnId: "policyNumber",
    width: 150,
    reorderable: true,
    resizable: true,
    sortable: true,
    label: "pct.policyNumber.label",
    cellType: "customtext",
    additionalInfo: {
      redColorFn: (entry) =>
        isMatched(entry) &&
        entry["policyNumberPaymentPlanEntry"] !== null &&
        entry["policyNumberPaymentPlanEntry"] !== undefined &&
        entry["policyNumber"] !== entry["policyNumberPaymentPlanEntry"] &&
        // the insurer may report the policy under its sub number - that counts as a match too
        entry["policyNumber"] !== entry["policySubNumber"],
    },
  },
  {
    columnId: "premiumAmount",
    width: 110,
    reorderable: true,
    resizable: true,
    sortable: true,
    label: "pct.premiumAmount.label",
    cellType: "customnumber",
    additionalInfo: {
      redColorFn: (entry) => {
        const counterpart = premiumAmountCounterpart(entry);
        return (
          isMatched(entry) &&
          counterpart !== null &&
          counterpart !== undefined &&
          entry["premiumAmount"] !== counterpart
        );
      },
    },
  },
  {
    columnId: "premiumAmountWithoutTax",
    width: 110,
    reorderable: true,
    resizable: true,
    sortable: true,
    label: "pct.premiumAmountWithoutTax.label",
    cellType: "customnumber",
    additionalInfo: {
      redColorFn: (entry) =>
        isPlanMatched(entry) &&
        entry["premiumAmountWithoutTaxPaymentPlanEntry"] !== null &&
        entry["premiumAmountWithoutTaxPaymentPlanEntry"] !== undefined &&
        entry["premiumAmountWithoutTax"] !==
          entry["premiumAmountWithoutTaxPaymentPlanEntry"],
    },
  },
  {
    columnId: "commissionRate",
    width: 90,
    reorderable: true,
    resizable: true,
    sortable: true,
    label: "pct.commissionRate.label",
    cellType: "customnumber",
    additionalInfo: {
      redColorFn: (entry) =>
        isMatched(entry) &&
        entry["commissionRatePaymentPlanEntry"] !== null &&
        entry["commissionRatePaymentPlanEntry"] !== undefined &&
        entry["commissionRate"] !== entry["commissionRatePaymentPlanEntry"],
    },
  },
  {
    columnId: "commissionAmount",
    width: 110,
    reorderable: true,
    resizable: true,
    sortable: true,
    label: "pct.commissionAmount.label",
    cellType: "customnumber",
    additionalInfo: {
      redColorFn: (entry) =>
        isMatched(entry) &&
        entry["commissionAmountPaymentPlanEntry"] !== null &&
        entry["commissionAmountPaymentPlanEntry"] !== undefined &&
        entry["commissionAmount"] !==
          entry["commissionAmountPaymentPlanEntry"],
    },
  },
  {
    columnId: "isBrokerPremiumCollection",
    width: 70,
    reorderable: true,
    resizable: true,
    sortable: true,
    label: "pct.isBrokerPremiumCollection.label",
    cellType: "customcheckmark",
    additionalInfo: {
      redColorFn: (entry) =>
        isPlanMatched(entry) &&
        entry["isBrokerPremiumCollectionPaymentPlanEntry"] !== null &&
        entry["isBrokerPremiumCollectionPaymentPlanEntry"] !== undefined &&
        entry["isBrokerPremiumCollection"] !==
          entry["isBrokerPremiumCollectionPaymentPlanEntry"],
    },
  },
  {
    columnId: "reportNet",
    width: 110,
    reorderable: true,
    resizable: true,
    sortable: true,
    label: "pct.reportNet.label",
    cellType: "customnumber",
  },
  {
    columnId: "policyNumberPaymentPlanEntry",
    width: 150,
    reorderable: true,
    resizable: true,
    sortable: true,
    label: "pct.policyNumberPaymentPlanEntry.label",
    cellType: "customtext",
    additionalInfo: {
      gosLink: {
        idField: "policyIdPaymentPlanEntry",
        linkType: "policy",
      },
    },
  },
  {
    columnId: "policySubNumber",
    width: 120,
    reorderable: true,
    resizable: true,
    sortable: true,
    label: "pct.policySubNumber.label",
    cellType: "customtext",
  },
  {
    columnId: "premiumAmountPaymentPlanEntry",
    width: 110,
    reorderable: true,
    resizable: true,
    sortable: true,
    label: "pct.premiumAmountPaymentPlanEntry.label",
    cellType: "customnumber",
  },
  {
    columnId: "premiumAmountWithoutTaxPaymentPlanEntry",
    width: 110,
    reorderable: true,
    resizable: true,
    sortable: true,
    label: "pct.premiumAmountWithoutTaxPaymentPlanEntry.label",
    cellType: "customnumber",
  },
  {
    columnId: "premiumAmountPayment",
    width: 110,
    reorderable: true,
    resizable: true,
    sortable: true,
    label: "pct.premiumAmountPayment.label",
    cellType: "customnumber",
  },
  {
    columnId: "commissionRatePaymentPlanEntry",
    width: 90,
    reorderable: true,
    resizable: true,
    sortable: true,
    label: "pct.commissionRatePaymentPlanEntry.label",
    cellType: "customnumber",
  },
  {
    columnId: "commissionAmountPaymentPlanEntry",
    width: 110,
    reorderable: true,
    resizable: true,
    sortable: true,
    label: "pct.commissionAmountPaymentPlanEntry.label",
    cellType: "customnumber",
  },
  {
    columnId: "isBrokerPremiumCollectionPaymentPlanEntry",
    width: 70,
    reorderable: true,
    resizable: true,
    sortable: true,
    label: "pct.isBrokerPremiumCollectionPaymentPlanEntry.label",
    cellType: "customcheckmark",
  },
  {
    columnId: "bookingDateFormatted",
    width: 100,
    reorderable: true,
    resizable: true,
    sortable: true,
    label: "pct.bookingDate.label",
    cellType: "customtext",
  },
  {
    columnId: "paymentDateFormatted",
    width: 100,
    reorderable: true,
    resizable: true,
    sortable: true,
    label: "pct.paymentDate.label",
    cellType: "customtext",
  },
  {
    columnId: "options",
    width: 110,
    reorderable: true,
    resizable: true,
    sortable: false,
    label: "pct.options.label",
    cellType: "insurerreportentryoptions",
    additionalInfo: {
      rowIdField: "insurerReportEntryId",
    },
  },
];
